import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';
import { OutputType as ListOutputType, SecretMetadata } from '../endpoints/secrets/list_GET.schema';
import { OutputType as CreateOutputType } from '../endpoints/secrets/create_POST.schema';
import { OutputType as UpdateOutputType } from '../endpoints/secrets/update_POST.schema';
import { OutputType as DeleteOutputType } from '../endpoints/secrets/delete_POST.schema';

const API_BASE = '/_api';

async function fetchApi<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...options,
  });

  const text = await response.text();
  const data = superjson.parse(text) as T & { error?: string };

  if (!response.ok || data.error) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

/**
 * Hook to fetch secrets for a session
 */
export function useSecrets(sessionId: string) {
  return useQuery({
    queryKey: ['secrets', sessionId],
    queryFn: () => fetchApi<ListOutputType>(`${API_BASE}/secrets/list?sessionId=${sessionId}`),
    enabled: !!sessionId,
  });
}

/**
 * Hook to create a new secret
 */
export function useCreateSecret() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      sessionId: string;
      keyName: string;
      value: string;
      description?: string;
    }) =>
      fetchApi<CreateOutputType>(`${API_BASE}/secrets/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify(data),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['secrets', variables.sessionId] });
    },
  });
}

/**
 * Hook to update a secret
 */
export function useUpdateSecret() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { secretId: string; value?: string; description?: string }) =>
      fetchApi<UpdateOutputType>(`${API_BASE}/secrets/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify(data),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['secrets', data.secret.sessionId] });
    },
  });
}

/**
 * Hook to delete a secret
 */
export function useDeleteSecret() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { secretId: string; sessionId: string }) =>
      fetchApi<DeleteOutputType>(`${API_BASE}/secrets/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify({ secretId: data.secretId }),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['secrets', variables.sessionId] });
    },
  });
}

export type { SecretMetadata };
